/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { useCallback } from 'preact/hooks';
import { classes } from '@adobe-commerce/elsie/lib';
import { Button as ElsieButton } from '@adobe-commerce/elsie/components';
import '@/auth/components/Button/Button.css';

export interface ButtonProps {
  type: 'submit' | 'button';
  variant?: 'primary' | 'secondary' | 'tertiary';
  className?: string;
  buttonText: string;
  enableLoader?: boolean;
  onClick?: (event: MouseEvent) => void;
  style?: Record<string, string | number>;
  icon?: VNode<HTMLAttributes<SVGSVGElement>>;
  disabled?: boolean;
}

export const Button: FunctionComponent<ButtonProps> = ({
  type,
  buttonText,
  variant,
  className = '',
  enableLoader = false,
  onClick,
  style,
  icon,
  ...props
}) => {
  const handleOnclick = useCallback(
    (event: MouseEvent) => {
      onClick?.(event);
    },
    [onClick]
  );

  const isLoader = enableLoader ? 'enableLoader' : '';

  return (
    <ElsieButton
      icon={icon}
      style={style}
      type={type}
      variant={variant}
      className={classes(['auth-button', className, isLoader])}
      onClick={handleOnclick}
      {...props}
    >
      <span className="auth-button__text">{buttonText}</span>
      {enableLoader ? (
        <div className="auth-button__wrapper">
          <span className="auth-button__loader" />
        </div>
      ) : null}
    </ElsieButton>
  );
};
